// src/pages/comercial/Objetivos.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import CriarObjetivos from "pages/comercial/CriarObjetivos";

// ------------------- Filtros por coluna -------------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

function numFmt(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function valoresResumo(row) {
  const parts = [];
  if (Number(row.qtd) > 0) parts.push(`QTD ${numFmt(row.qtd)}`);
  if (Number(row.euros) > 0) parts.push(`€ ${numFmt(row.euros)}`);
  if (Number(row.litros) > 0) parts.push(`L ${numFmt(row.litros)}`);
  if (Number(row.clientes) > 0) parts.push(`Cli ${numFmt(row.clientes)}`);
  return parts.join(" | ");
}

function TruncTooltipCell({ value, maxWidth = 220 }) {
  const [open, setOpen] = useState(false);
  const txt = (value ?? "").toString().trim();

  if (!txt) return <span />;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <span>
        <Tooltip
          title={txt}
          arrow
          placement="top"
          open={open}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <span
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen((v) => !v)} // tablet/click
            style={{
              display: "inline-block",
              maxWidth,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "middle",
              cursor: "pointer",
            }}
          >
            {txt}
          </span>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
}

const dateOnly = (v) => {
  if (!v) return "";
  return String(v).split("T")[0];
};

function hasAnyObjectiveSec(secData) {
  if (!secData) return false;
  const keys = ["qtd", "euros", "litros", "clientes"];
  return keys.some((k) => Number(secData?.[k]?.objetivo ?? 0) > 0);
}

function hasAnyObjectiveVendedores(vendedores, tipoKey) {
  const arr = Array.isArray(vendedores) ? vendedores : [];
  return arr.some((v) => Number(v?.[tipoKey]?.objetivo ?? 0) > 0);
}

function metricOrZero(m) {
  return {
    objetivo: Number(m?.objetivo ?? 0),
    vendas: Number(m?.vendas ?? 0),
  };
}

function difFmt(objetivo, real) {
  const o = Number(objetivo ?? 0);
  const r = Number(real ?? 0);
  if (!o) return "-";
  return numFmt(r - o);
}

function pctFmtAtingimento(objetivo, real) {
  const o = Number(objetivo ?? 0);
  const r = Number(real ?? 0);
  if (!o) return "-";
  const pct = (r / o) * 100;
  return `${pct.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
}

function difCell(objetivo, real) {
  const o = Number(objetivo ?? 0);
  const r = Number(real ?? 0);

  if (!o) return <span>-</span>;

  const dif = r - o;
  const color = dif > 0 ? "#4caf50" : dif < 0 ? "#d32f2f" : undefined;

  return <span style={{ color, fontWeight: 500 }}>{numFmt(dif)}</span>;
}

function SecResumoTable({ title, secData }) {
  const rows = useMemo(() => {
    const tipos = [
      { tipo: "Qtd", key: "qtd" },
      { tipo: "Euros", key: "euros" },
      { tipo: "Litros", key: "litros" },
      { tipo: "Clientes", key: "clientes" },
    ];

    return tipos.map((t) => {
      const m = metricOrZero(secData?.[t.key]);
      return {
        tipo: t.tipo,
        objetivo: m.objetivo,
        vendas: m.vendas,
      };
    });
  }, [secData]);

  const columns = useMemo(
    () => [
      { Header: "Tipo", accessor: "tipo" },
      {
        Header: "Objetivo",
        accessor: "objetivo",
        Cell: ({ value }) => (Number(value) === 0 ? "-" : numFmt(value)),
      },
      {
        Header: "Real",
        accessor: "vendas",
        Cell: ({ value }) => <strong>{numFmt(value)}</strong>,
      },
      {
        Header: "Dif.",
        id: "dif",
        Cell: ({ row }) => difCell(row.original.objetivo, row.original.vendas),
      },
      {
        Header: "%",
        id: "pct",
        Cell: ({ row }) => pctFmtAtingimento(row.original.objetivo, row.original.vendas),
      },
    ],
    []
  );

  const table = useTable({ columns, data: rows }, useSortBy);

  return (
    <div style={{ marginBottom: 18 }}>
      <MDTypography
        variant="button"
        fontWeight="medium"
        color="text"
        sx={{ display: "block", mb: 1 }}
      >
        {title}
      </MDTypography>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <table {...table.getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            {table.headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={col.id}
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      borderBottom: "1px solid rgba(0,0,0,0.12)",
                      fontSize: 12,
                      color: "#344767",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{col.render("Header")}</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...table.getTableBodyProps()}>
            {table.rows.map((row) => {
              table.prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{
                        padding: "10px 8px",
                        fontSize: 13,
                        color: "#344767",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendedoresTable({ vendedores, tipoKey }) {
  const rows = useMemo(() => {
    const arr = Array.isArray(vendedores) ? vendedores : [];
    return arr.map((v) => {
      const m = metricOrZero(v?.[tipoKey]);
      return {
        vendedor: v?.vendedor ?? "",
        refvendedor: v?.refvendedor ?? "",
        objetivo: m.objetivo,
        vendas: m.vendas,
      };
    });
  }, [vendedores, tipoKey]);

  const columns = useMemo(
    () => [
      {
        Header: "Vendedor",
        accessor: "vendedor",
        Cell: ({ row }) => <span title={row.original.refvendedor}>{row.original.vendedor}</span>,
      },
      {
        Header: "Objetivo",
        accessor: "objetivo",
        Cell: ({ value }) => (Number(value) === 0 ? "-" : numFmt(value)),
      },
      {
        Header: "Real",
        accessor: "vendas",
        Cell: ({ value }) => <strong>{numFmt(value)}</strong>,
      },
      {
        Header: "Dif.",
        id: "dif",
        Cell: ({ row }) => difCell(row.original.objetivo, row.original.vendas),
      },
      {
        Header: "%",
        id: "pct",
        Cell: ({ row }) => pctFmtAtingimento(row.original.objetivo, row.original.vendas),
      },
    ],
    []
  );

  const table = useTable({ columns, data: rows }, useSortBy);

  return (
    <div>
      <div style={{ width: "100%", overflowX: "auto" }}>
        <table {...table.getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            {table.headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={col.id}
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      borderBottom: "1px solid rgba(0,0,0,0.12)",
                      fontSize: 12,
                      color: "#344767",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{col.render("Header")}</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...table.getTableBodyProps()}>
            {table.rows.map((row) => {
              table.prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{
                        padding: "10px 8px",
                        fontSize: 13,
                        color: "#344767",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ padding: 12, color: "#67748e" }}>
                  Sem dados de vendedores.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Objetivos() {
  const { showAlert } = useAlert();
  const { temPermissao, temFuncao } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDel, setOpenDel] = useState(false);
  const [objDel, setObjDel] = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  // Dialog editor
  const [openEditor, setOpenEditor] = useState(false);
  const [idEdit, setIdEdit] = useState(null);

  const [openDetalhes, setOpenDetalhes] = useState(false);
  const [detLoading, setDetLoading] = useState(false);
  const [detObj, setDetObj] = useState(null);
  const [detData, setDetData] = useState(null);

  const abrirDetalhes = async (obj) => {
    if (!obj?.id) return;

    setDetObj(obj);
    setDetData(null);
    setOpenDetalhes(true);

    setDetLoading(true);
    const [payload, err] = await handleApi(
      api.post("Comercial/SelObjetivoDetalhes", { id: obj.id })
    );
    setDetLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    console.log("payload", payload);

    setDetData(payload || null);
  };

  const podeApagar = temFuncao("Administração") || temFuncao("Informática");

  const pedirApagar = (obj) => {
    if (!podeApagar) return;
    setObjDel(obj);
    setOpenDel(true);
  };

  const confirmarApagar = async () => {
    if (!podeApagar || !objDel?.id) return;

    setDelLoading(true);
    const [_, err, meta] = await handleApi(api.post("Comercial/DelObjetivo", { id: objDel.id }));
    setDelLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message ?? "Objetivo apagado.", meta?.severity ?? "success");
    setOpenDel(false);
    setObjDel(null);
    await carregar();
  };

  const carregar = async () => {
    setLoading(true);
    const [payload, err] = await handleApi(api.post("Comercial/SelObjetivos", {}));
    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }
    setData(Array.isArray(payload) ? payload : []);
  };

  useEffect(() => {
    (async () => {
      await carregar();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copiarObjetivo = async (obj) => {
    if (!obj?.id) return;

    setLoading(true);
    const [payload, err, meta] = await handleApi(
      api.post("Comercial/CopiarObjetivo", { id: obj.id })
    );
    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message ?? "Objetivo copiado.", meta?.severity ?? "success");

    // Recarrega a lista
    await carregar();

    // UX opcional: abrir logo o editor do novo objetivo
    if (payload?.id) {
      setIdEdit(payload.id);
      setOpenEditor(true);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 90,
        minWidth: 80,
        maxWidth: 110,
        Cell: ({ row }) => {
          const o = row.original;

          const podeApagar = temFuncao("Administração") || temFuncao("Informática");

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => abrirDetalhes(o)}
                sx={{
                  fontSize: 11,
                  textTransform: "none",
                  padding: "2px 8px",
                  minWidth: "auto",
                }}
              >
                Detalhes
              </MDButton>
              <IconButton
                size="small"
                color="primary"
                disabled={!temPermissao("Menu Administração", "u")}
                title="Editar"
                onClick={() => {
                  setIdEdit(o.id);
                  setOpenEditor(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>

              {podeApagar && (
                <IconButton
                  size="small"
                  color="error"
                  title="Apagar"
                  onClick={() => pedirApagar(o)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}

              <IconButton
                size="small"
                title="Copiar objetivo"
                disabled={!temPermissao("Menu Administração", "u")}
                onClick={() => copiarObjetivo(row.original)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },

      // NOTA: removemos a coluna ID da tabela (não aparece)

      { Header: "Nome", accessor: "objetivo_nome", Filter: TextFilter },

      {
        Header: "Início",
        accessor: "dataini",
        Filter: TextFilter,
        Cell: ({ value }) => dateOnly(value),
      },
      {
        Header: "Fim",
        accessor: "datafim",
        Filter: TextFilter,
        Cell: ({ value }) => dateOnly(value),
      },

      {
        Header: "Fabricantes",
        accessor: "fabricantes_nomes",
        Filter: TextFilter,
        Cell: ({ value }) => <TruncTooltipCell value={value} maxWidth={260} />,
      },
      {
        Header: "Famílias",
        accessor: "familias_nomes",
        Filter: TextFilter,
        Cell: ({ value }) => <TruncTooltipCell value={value} maxWidth={240} />,
      },

      //   {
      //     Header: "Valores (fabricante)",
      //     id: "valores",
      //     disableFilters: true,
      //     Cell: ({ row }) => (
      //       <span title={valoresResumo(row.original)} style={{ fontSize: 13 }}>
      //         {valoresResumo(row.original)}
      //       </span>
      //     ),
      //   },
    ],
    [temPermissao, temFuncao] // importante
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {},
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const abrirNovo = () => {
    setIdEdit(null);
    setOpenEditor(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox mt={2}>
        <Card>
          <MDBox p={2} display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
            <MDButton
              color="success"
              variant="contained"
              //   startIcon={<AddIcon />}
              onClick={abrirNovo}
              disabled={!temPermissao("Menu Administração", "w")}
            >
              Novo Objetivo
            </MDButton>
          </MDBox>

          <MDBox p={2} pt={0}>
            {loading ? (
              <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
                <CircularProgress />
              </MDBox>
            ) : (
              <div style={{ width: "100%", overflowX: "auto" }}>
                <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {headerGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            key={col.id}
                            style={{
                              textAlign: "left",
                              padding: "10px 8px",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              fontSize: 12,
                              color: "#344767",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span>{col.render("Header")}</span>
                              <span style={{ fontSize: 11, opacity: 0.7 }}>
                                {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                              </span>
                            </div>

                            {!col.disableFilters && (
                              <div style={{ marginTop: 6 }}>{col.render("Filter")}</div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row);
                      return (
                        <tr
                          {...row.getRowProps()}
                          key={row.id}
                          style={{
                            borderBottom: "1px solid rgba(0,0,0,0.05)",
                          }}
                        >
                          {row.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id}
                              style={{
                                padding: "10px 8px",
                                fontSize: 13,
                                color: "#344767",
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={columns.length} style={{ padding: 16, color: "#67748e" }}>
                          Sem dados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Editor em Dialog */}
      <Dialog
        open={openEditor}
        onClose={() => setOpenEditor(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            height: "92vh",
            maxHeight: "92vh",
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: "100%", overflow: "auto" }}>
          <CriarObjetivos
            embed
            idObjetivo={idEdit}
            onClose={() => setOpenEditor(false)}
            onSaved={async () => {
              setOpenEditor(false);
              await carregar();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDel}
        onClose={() => (delLoading ? null : setOpenDel(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Apagar objetivo</DialogTitle>

        <DialogContent dividers>
          <MDTypography variant="body2">
            Tens a certeza que queres apagar o objetivo <b>{objDel?.objetivo_nome}</b> (início:{" "}
            <b>{objDel?.dataini?.slice(0, 10)}</b>)?
          </MDTypography>

          <MDBox mt={2}>
            <MDTypography variant="body2" color="text">
              Isto remove também famílias, fabricantes, equipa, vendedores, clientes, etc.
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenDel(false)} disabled={delLoading}>
            Cancelar
          </MDButton>

          <MDButton color="error" onClick={confirmarApagar} disabled={delLoading}>
            {delLoading ? "A apagar..." : "Apagar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openDetalhes} onClose={() => setOpenDetalhes(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalhes — {detObj?.objetivo_nome || `Objetivo #${detObj?.id || ""}`}
        </DialogTitle>

        <DialogContent dividers>
          {detLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12 }}>
              <CircularProgress size={20} />
              <span>A calcular diferenças…</span>
            </div>
          )}

          {!detLoading && !detData && <div style={{ padding: 12, opacity: 0.8 }}>Sem dados.</div>}

          {!detLoading && detData && (
            <div>
              {hasAnyObjectiveSec(detData?.fabricante) && (
                <>
                  <SecResumoTable title="Objetivos do Fabricante" secData={detData?.fabricante} />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveSec(detData?.equipa) && (
                <>
                  <SecResumoTable title="Objetivos da Equipa" secData={detData?.equipa} />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveSec(detData?.individuais) && (
                <>
                  <SecResumoTable
                    title="Objetivos Individuais (Totais)"
                    secData={detData?.individuais}
                  />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(detData?.individuais_vendedores, "qtd") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Quantidades
                  </MDTypography>
                  <VendedoresTable vendedores={detData?.individuais_vendedores} tipoKey="qtd" />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(detData?.individuais_vendedores, "euros") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Euros
                  </MDTypography>
                  <VendedoresTable vendedores={detData?.individuais_vendedores} tipoKey="euros" />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(detData?.individuais_vendedores, "litros") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Litros
                  </MDTypography>
                  <VendedoresTable vendedores={detData?.individuais_vendedores} tipoKey="litros" />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(detData?.individuais_vendedores, "clientes") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Clientes
                  </MDTypography>
                  <VendedoresTable
                    vendedores={detData?.individuais_vendedores}
                    tipoKey="clientes"
                  />
                </>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenDetalhes(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
